import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { quoteService } from '../../services/quoteService';
import { documentService } from '../../services/documentService';
import { DOCUMENT_TYPES } from '../../config';

function ViewQuotePage() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();

  const [quote, setQuote] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAccepting, setIsAccepting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [showPrintMenu, setShowPrintMenu] = useState(false);
  const [isGeneratingDocument, setIsGeneratingDocument] = useState(false);
  const [documentError, setDocumentError] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({ email: '', customerName: '' });
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState('');

  // Helper to format dates - adapt as needed
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (e) {
      console.error('Failed to parse date:', dateString, e);
      return dateString; // Fallback to original string if parsing fails
    }
  };

  useEffect(() => {
    const fetchQuote = async () => {
      if (!quoteId) {
        setError('No Quote ID specified.');
        setIsLoading(false);
        return;
      }

      if (!token) {
        setError('Authentication required. Please log in.');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError('');
        
        const response = await quoteService.getQuoteById(quoteId);
        
        // The backend returns the quote data directly, not nested in a data.quote structure
        if (response && response.id) {
          setQuote(response);
        } else if (response.status === 'success' && response.data?.quote) {
          // Fallback for different response format
          setQuote(response.data.quote);
        } else if (response.quote) {
          // Another fallback
          setQuote(response.quote);
        } else {
          setError('Invalid response format from server.');
        }
      } catch (err) {
        console.error('Error fetching quote:', err);
        setError(err.message || 'Failed to fetch quote details.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchQuote();
  }, [quoteId, token]);

  const handleAcceptQuote = async () => {
    if (!user || user.role !== 'client') {
      setError('Only clients can accept quotes.');
      return;
    }

    setIsAccepting(true);
    setError('');

    try {
      const response = await quoteService.acceptQuote(quoteId);
      // Handle direct response format
      if (response && response.id) {
        setQuote(response);
      } else if (response.status === 'success' && response.data?.quote) {
        setQuote(response.data.quote);
      } else if (response.quote) {
        setQuote(response.quote);
      }
    } catch (err) {
      console.error('Error accepting quote:', err);
      setError(err.message || 'Failed to accept quote.');
    } finally {
      setIsAccepting(false);
    }
  };

  const handleRejectQuote = async () => {
    if (!user || user.role !== 'client') {
      setError('Only clients can reject quotes.');
      return;
    }

    setIsRejecting(true);
    setError('');

    try {
      const response = await quoteService.rejectQuote(quoteId);
      // Handle direct response format
      if (response && response.id) {
        setQuote(response);
      } else if (response.status === 'success' && response.data?.quote) {
        setQuote(response.data.quote);
      } else if (response.quote) {
        setQuote(response.quote);
      }
    } catch (err) {
      console.error('Error rejecting quote:', err);
      setError(err.message || 'Failed to reject quote.');
    } finally {
      setIsRejecting(false);
    }
  };

  const handleConvertToInvoice = async () => {
    if (!user || !['admin', 'manager', 'staff'].includes(user.role)) {
      setError('You do not have permission to convert quotes to invoices.');
      return;
    }

    setIsConverting(true);
    setError('');

    try {
      const response = await quoteService.convertToInvoice(quoteId);
      // Handle different possible response formats for conversion
      if (response && response.invoice && response.invoice.id) {
        // Direct response with invoice object
        navigate(`/admin/invoices/view/${response.invoice.id}`);
      } else if (response.status === 'success' && response.data?.invoice) {
        // Nested response format
        navigate(`/admin/invoices/view/${response.data.invoice.id}`);
      } else if (response.id) {
        // Response is the invoice directly
        navigate(`/admin/invoices/view/${response.id}`);
      } else {
        setError('Failed to convert quote to invoice.');
      }
    } catch (err) {
      console.error('Error converting quote to invoice:', err);
      setError(err.message || 'Failed to convert quote to invoice.');
    } finally {
      setIsConverting(false);
    }
  };

  // Document handling functions
  const handleDownloadPdf = async () => {
    setIsGeneratingDocument(true);
    setDocumentError('');
    try {
      await documentService.downloadPdfFile(DOCUMENT_TYPES.QUOTE, quoteId, `Quote-${quote.quoteNumber || quoteId}.pdf`);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setDocumentError('Failed to download PDF. Please try again.');
    } finally {
      setIsGeneratingDocument(false);
      setShowPrintMenu(false);
    }
  };

  const handleViewPdf = () => {
    try {
      documentService.viewPdfInNewWindow(DOCUMENT_TYPES.QUOTE, quoteId);
      setShowPrintMenu(false);
    } catch (error) {
      console.error('Error viewing PDF:', error);
      setDocumentError('Failed to open PDF viewer. Please try again.');
    }
  };

  const handlePreviewHtml = async () => {
    setIsGeneratingDocument(true);
    setDocumentError('');
    try {
      await documentService.previewInPopup(DOCUMENT_TYPES.QUOTE, quoteId);
    } catch (error) {
      console.error('Error previewing HTML:', error);
      setDocumentError('Failed to generate HTML preview. Please try again.');
    } finally {
      setIsGeneratingDocument(false);
      setShowPrintMenu(false);
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    setIsSendingEmail(true);
    setDocumentError('');
    setEmailSuccess('');
    
    try {
      await documentService.sendEmail(
        DOCUMENT_TYPES.QUOTE,
        quoteId,
        emailData.email || quote.client?.email,
        emailData.customerName || quote.client?.username
      );
      setEmailSuccess('Quote sent successfully!');
      setTimeout(() => {
        setShowEmailModal(false);
        setEmailSuccess('');
        setEmailData({ email: '', customerName: '' });
      }, 2000);
    } catch (error) {
      console.error('Error sending email:', error);
      setDocumentError(error.message || 'Failed to send email. Please try again.');
    } finally {
      setIsSendingEmail(false);
    }
  };

  if (isLoading) {
    return <div className="container mx-auto px-4 py-8 text-center"><p>Loading quote details for #{quoteId}...</p></div>;
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Link to="/admin/quotes" className="text-blue-600 hover:text-blue-800 mb-4 inline-block">&larr; Back to Quotes</Link>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Link to="/admin/quotes" className="text-blue-600 hover:text-blue-800 mb-4 inline-block">&larr; Back to Quotes</Link>
        <p>No quote data found for #{quoteId}.</p>
      </div>
    );
  }

  // Determine if the quote can be edited or converted (example logic)
  const canBeEdited = (quote.status === 'draft' || quote.status === 'sent_to_client');
  // const canBeConvertedToEstimate = (quote.status === 'accepted_by_client' || quote.status === 'sent_to_client' || quote.status === 'draft');
  // const canBeConvertedToInvoice = (quote.status === 'accepted_by_client');

  return (
    <div className="container mx-auto px-4 py-8">
      <header className="mb-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-brand-text-dark">Quote Details: {quote.quoteNumber || `#${quote.id}`}</h1>
          <Link to="/admin/quotes" className="text-sm text-blue-600 hover:text-blue-800">&larr; Back to All Quotes</Link>
        </div>
      </header>

      <div className="bg-white shadow-lg rounded-lg p-6 md:p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 pb-4 border-b">
          <div>
            <h2 className="text-sm font-medium text-gray-500">Customer Name</h2>
            <p className="mt-1 text-lg text-gray-900">{quote.client?.username || quote.customerName || 'N/A'}</p>
          </div>
          <div>
            <h2 className="text-sm font-medium text-gray-500">Customer Email</h2>
            <p className="mt-1 text-lg text-gray-900">{quote.client?.email || quote.customerEmail || 'N/A'}</p>
          </div>
          <div>
            <h2 className="text-sm font-medium text-gray-500">Quote Date</h2>
            <p className="mt-1 text-lg text-gray-900">{formatDate(quote.quoteDate)}</p>
          </div>
          <div>
            <h2 className="text-sm font-medium text-gray-500">Expiry Date</h2>
            <p className="mt-1 text-lg text-gray-900">{formatDate(quote.expiryDate)}</p>
          </div>
          <div>
            <h2 className="text-sm font-medium text-gray-500">Status</h2>
            <p className="mt-1 text-lg">
              <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${ 
                quote.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                quote.status === 'sent_to_client' ? 'bg-blue-100 text-blue-800' :
                quote.status === 'accepted_by_client' ? 'bg-green-100 text-green-800' :
                quote.status === 'rejected_by_client' ? 'bg-red-100 text-red-800' :
                quote.status === 'converted_to_estimate' ? 'bg-purple-100 text-purple-800' :
                quote.status === 'converted_to_invoice' ? 'bg-teal-100 text-teal-800' :
                quote.status === 'expired' ? 'bg-red-100 text-red-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {quote.status ? quote.status.replace(/_/g, ' ').toUpperCase() : 'UNKNOWN'}
              </span>
            </p>
          </div>
           {quote.serviceRequestId && (
             <div>
               <h2 className="text-sm font-medium text-gray-500">Linked Service Request</h2>
               <p className="mt-1 text-lg">
                <Link to={`/admin/service-requests/${quote.serviceRequestId}`} className="text-blue-600 hover:text-blue-800">
                    View Request #{quote.serviceRequestId}
                </Link>
               </p>
             </div>
           )}
        </div>

        {quote.notes && (
          <div className="mb-6">
            <h2 className="text-sm font-medium text-gray-500">Notes / Scope of Work</h2>
            <p className="mt-1 text-gray-700 whitespace-pre-wrap">{quote.notes}</p>
          </div>
        )}
        
        {quote.lineItems && quote.lineItems.length > 0 ? (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Line Items</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item/Service</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Price</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {quote.lineItems.map((item, index) => (
                    <tr key={item.id || index}>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{item.description}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 text-right">{item.quantity}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 text-right">${parseFloat(item.unitPrice || 0).toFixed(2)}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 font-semibold text-right">${parseFloat(item.total || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : <p className="text-gray-500 mb-6">No line items found for this quote.</p> }

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 border-t pt-6">
          <div>
            <p className="text-sm text-gray-500">Subtotal:</p>
            <p className="text-lg text-gray-700 font-semibold text-right">${parseFloat(quote.subTotal || 0).toFixed(2)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Tax ({ ((quote.taxRate || 0) * 100).toFixed(2) }%):</p>
            <p className="text-lg text-gray-700 font-semibold text-right">${parseFloat(quote.taxAmount || 0).toFixed(2)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Total Amount:</p>
            <p className="text-xl text-gray-800 font-bold text-right">${parseFloat(quote.totalAmount || 0).toFixed(2)}</p>
          </div>
        </div>

        {quote.termsAndConditions && (
          <div className="mb-6 pt-4 border-t">
            <h4 className="text-md font-semibold text-gray-700 mb-1">Terms & Conditions:</h4>
            <p className="text-sm text-gray-600 whitespace-pre-wrap">{quote.termsAndConditions}</p>
          </div>
        )}

        <div className="mt-8 flex flex-wrap justify-end space-x-0 sm:space-x-3 space-y-3 sm:space-y-0">
          <div className="relative inline-block">
            <button 
              onClick={() => setShowPrintMenu(!showPrintMenu)} 
              className="w-full sm:w-auto bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-150 ease-in-out flex items-center"
              disabled={isGeneratingDocument}
            >
              {isGeneratingDocument ? 'Processing...' : 'Print Quote'}
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
            
            {showPrintMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10">
                <div className="py-1">
                  <button 
                    onClick={handleDownloadPdf}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Download PDF
                  </button>
                  <button 
                    onClick={handleViewPdf}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    View PDF in Browser
                  </button>
                  <button 
                    onClick={handlePreviewHtml}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Preview HTML
                  </button>
                  <button 
                    onClick={() => window.print()}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Print Current Page
                  </button>
                </div>
              </div>
            )}
          </div>
          
          <button
            onClick={() => setShowEmailModal(true)}
            className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-150 ease-in-out"
          >
            Send Email
          </button>
          
          {documentError && (
            <div className="w-full mt-2 text-red-600 text-sm">
              {documentError}
            </div>
          )}
          
          {canBeEdited && (
            <button 
              onClick={() => navigate(`/admin/quotes/edit/${quote.id}`)} 
              className="w-full sm:w-auto bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-150 ease-in-out"
            >
              Edit Quote
            </button>
          )}
          
          {/* Client Actions */}
          {user?.role === 'client' && quote.status === 'sent_to_client' && (
            <>
              <button 
                onClick={handleAcceptQuote}
                disabled={isAccepting}
                className="w-full sm:w-auto bg-green-500 hover:bg-green-600 disabled:bg-green-300 text-white font-semibold py-2 px-4 rounded-lg transition duration-150 ease-in-out"
              >
                {isAccepting ? 'Accepting...' : 'Accept Quote'}
              </button>
              <button 
                onClick={handleRejectQuote}
                disabled={isRejecting}
                className="w-full sm:w-auto bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white font-semibold py-2 px-4 rounded-lg transition duration-150 ease-in-out"
              >
                {isRejecting ? 'Rejecting...' : 'Reject Quote'}
              </button>
            </>
          )}
          
          {/* Admin/Manager/Staff Actions */}
          {['admin', 'manager', 'staff'].includes(user?.role) && quote.status === 'accepted_by_client' && (
            <button 
              onClick={handleConvertToInvoice}
              disabled={isConverting}
              className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white font-semibold py-2 px-4 rounded-lg transition duration-150 ease-in-out"
            >
              {isConverting ? 'Converting...' : 'Convert to Invoice'}
            </button>
          )}
        </div>
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Send Quote via Email</h3>
              <form onSubmit={handleSendEmail}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={emailData.email || quote?.client?.email || ''}
                    onChange={(e) => setEmailData({ ...emailData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="customer@example.com"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer Name (optional)
                  </label>
                  <input
                    type="text"
                    value={emailData.customerName || quote?.client?.username || ''}
                    onChange={(e) => setEmailData({ ...emailData, customerName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Customer Name"
                  />
                </div>
                {emailSuccess && (
                  <div className="mb-4 p-2 bg-green-100 text-green-700 rounded">
                    {emailSuccess}
                  </div>
                )}
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEmailModal(false);
                      setEmailData({ email: '', customerName: '' });
                      setDocumentError('');
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition duration-150 ease-in-out"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSendingEmail}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded-md transition duration-150 ease-in-out"
                  >
                    {isSendingEmail ? 'Sending...' : 'Send Email'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ViewQuotePage;
